<template>
  <div class="navigation">
    <span
      class="visually-hidden"
      v-text="$string('general.site_header.navigation')"
    />

    <nav class="navigation__nav container">
      <ul class="navigation__list">
        <li
          v-for="(tier1) of desktop.items"
          :key="tier1.handle"
          class="navigation__item"
        >
          <button
            v-if="tier1.links.length"
            :aria-expanded="isTier1ButtonActive(tier1.title)"
            aria-haspopup="dialog"
            class="
              navigation__link
              navigation__link--button
              text-body-s-regular
            "
            :class="{
              [$class('active')]: isTier1ButtonActive(tier1.title),
            }"
            :style="{
              color: getLinkSpecialColor(tier1),
            }"
            type="button"
            @blur="handleNavBlur"
            @focus="() => false"
            @mouseenter="handleNavOpen(tier1)"
            @mouseleave="handleNavClose()"
          >
            <a
              class="navigation__direct-link text-body-s-regular"
              :href="tier1.url"
              :style="{
                color: getLinkSpecialColor(tier1),
              }"
              @click.stop
              v-text="tier1.title"
            />
          </button>

          <a
            v-else
            class="
              navigation__link
              navigation__link--anchor
              text-body-s-regular
            "
            :href="tier1.url"
            :style="{
              color: getLinkSpecialColor(tier1),
            }"
            v-text="tier1.title"
          />
        </li>
      </ul>
    </nav>

    <!-- Meganav -->
    <!-- eslint-disable vuejs-accessibility/aria-props -->
    <overlay
      key="navigation-tiers"
      :aria-labelledby-override="navigationTiersAriaLabelledby"
      block-class="navigation-tiers-overlay"
      :disable-scroll="false"
      direction="fade"
      :namespace="namespace"
      :template="false"
      type="shelf"
    >
      <template #body="overlay">
        <navigation-tiers
          v-if="getComponentVisible('navigation-tiers')"
          :overlay="overlay"
          :tier="meganav.currentMenu"
          @blur="handleNavClose()"
          @focus="handleNavEnter"
          @keydown.esc="handleNavClose(true)"
          @mouseenter="handleNavEnter"
          @mouseleave="handleNavClose()"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Navigation (navigation)
 * -----------------------------------------------------------------------------
 * Desktop navigation links.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import Overlay from '~global/overlay/overlay'

import { specialLinks } from '~/config/navigations'
import { convertToHandle } from '~/helpers/convert'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Navigation',

  components: {
    NavigationTiers: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.navigationTiers' */
        '~global/navigation-tiers/navigation-tiers'
      ),
    }),
    Overlay,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
  },

  data() {
    return {
      currentLink: false,
      meganav: {
        closing: false,
        currentMenu: null,
        escape: false,
        open: false,
      },
      namespace: 'navigationTiers',
      navCloseTimeout: false,
      navDelay: this.$timing('quick'),
      navOpenTimeout: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Return the aria-labelledby of the navigation tiers overlay based on the
     * focussed menu button.
     * @returns {String}
     */
    navigationTiersAriaLabelledby() {
      return this.meganav.currentMenu?.title
        ? `overlay-${this.namespace}-${convertToHandle(this.meganav.currentMenu.title)}`
        : ''
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handles the action when the nav closes.
     * @param {Boolean} closeWithEscape - Closing mega nav by escaping.
     */
    handleNavClose(closeWithEscape = false) {
      clearTimeout(this.navOpenTimeout)

      if (!this.getActiveOverlay || this.meganav.closing) {
        return
      }

      this.meganav.closing = true
      this.meganav.escape = closeWithEscape

      this.navCloseTimeout = setTimeout(() => {
        this.meganav.closing = false
        this.meganav.currentMenu = null
        this.meganav.escape = false
        this.meganav.open = false

        if (closeWithEscape) {
          return
        }

        this.closeOverlay()
      }, this.navDelay)
    },

    /**
     * Handles opening the nav and setting the appropriate tier.
     * @param {Object} tier - The tier that is opening on the nav
     */
    handleNavOpen(tier) {
      const linksWithoutColor = this.getLinksWithoutColor(tier)

      if (!linksWithoutColor.length || this.meganav.escape) {
        return
      }

      clearTimeout(this.navCloseTimeout)
      clearTimeout(this.navOpenTimeout)

      this.navOpenTimeout = setTimeout(() => {
        this.meganav.closing = false
        this.meganav.currentMenu = tier
        this.meganav.escape = false
        this.meganav.open = true

        this.openOverlay({
          component: 'navigation-tiers',
          ignoreDismissed: true,
          namespace: 'navigationTiers',
        })
      }, this.navDelay)
    },

    /**
     * Handles the mouse enter event on the nav.
     */
    handleNavEnter() {
      this.meganav.closing = false
      this.meganav.escape = false
      clearTimeout(this.navCloseTimeout)
      clearTimeout(this.navOpenTimeout)
    },

    /**
     * Handle navigation blur.
     */
    handleNavBlur() {
      clearTimeout(this.navOpenTimeout)
    },

    /**
     * Return if the current button matches the active overlay.
     * @param {String} title - Link title.
     * @returns {Boolean}
     */
    isTier1ButtonActive(title) {
      return this.meganav.open && this.meganav.currentMenu?.title === title
    },

    /**
     * Get links without color.
     * @param {Object} tier - Menu item.
     * @returns {Array}
     */
    getLinksWithoutColor(tier) {
      return tier.links.filter((link) => {
        return !link.title.includes(specialLinks.color)
      })
    },

    /**
     * Check if the tier has a color inside its links.
     * @param {object} link
     * @return {string}
     */
    getLinkSpecialColor(link) {
      return link.links?.find((item) => {
        return item.title.includes(specialLinks.color)
      })?.title?.replace(specialLinks.color, '')
    },
  },
}
</script>
